import { getPopupMaxHeight } from 'components/dropdown/DropdownPopup';
import { usePopup } from 'custom_hooks/usePopup';
import { ReactNode, useMemo } from 'react';
import { BadgeWrapper } from 'shared/badge_icon/BadgeWrapper';
import {
    MultiSelectQuickFilterOption,
    MultiSelectQuickFilterPopup,
} from 'shared/filter_pane/MultiSelectQuickFilterPopup';
import { QuickFilterToggle } from 'shared/filter_pane/QuickFilterToggle';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const StyledQuickFilterToggle = styled(QuickFilterToggle)<{
    $isOpen: boolean;
    isToggled: boolean;
}>`
    background-color: ${({ $isOpen, isToggled }) =>
        $isOpen && !isToggled ? tokens.border.default : ''} !important;
`;

export type MultiSelectQuickFilterButtonProps<TValue> = {
    /**
     * Label displayed on the button
     */
    label: string;
    /**
     * Unique ID for the button element
     */
    id: string;
    /**
     * Currently selected values
     */
    selectedValues: TValue[];
    /**
     * Callback when selected values change
     */
    onChangeValues: (values: TValue[]) => void;
    /**
     * All available options to display in the popup.
     * Should already be filtered and sorted as desired.
     */
    options: MultiSelectQuickFilterOption<TValue>[];
    /**
     * Optional custom badge content (if not provided, shows count)
     */
    badgeContent?: ReactNode;
};

export function MultiSelectQuickFilterButton<TValue>({
    label,
    id,
    selectedValues,
    onChangeValues,
    options,
    badgeContent,
}: MultiSelectQuickFilterButtonProps<TValue>) {
    const { isOpen, close, toggleOpen, triggerRef, popupRef } = usePopup({
        closeOnClickOutside: true,
    });

    const adjustedMaxHeight = getPopupMaxHeight(
        triggerRef.current,
        window.innerHeight,
        false,
    );

    const maxHeight = `max(8rem, min(50rem, ${adjustedMaxHeight}))`;

    const badgeNumber = useMemo(() => {
        return badgeContent !== undefined ? undefined : selectedValues.length;
    }, [badgeContent, selectedValues.length]);

    return (
        <>
            <BadgeWrapper
                number={badgeNumber}
                textColor={tokens.text.primary}
                position={{
                    corner: 'topright',
                    inset: '-8px',
                }}
                backgroundColor={tokens.surface.raised}
                badgeHidden={selectedValues.length === 0}
            >
                <StyledQuickFilterToggle
                    ref={triggerRef}
                    id={id}
                    isToggled={selectedValues.length > 0}
                    onToggle={toggleOpen}
                    $isOpen={isOpen}
                    onBlur={(e) => {
                        if (
                            e.relatedTarget &&
                            e.relatedTarget !== triggerRef.current &&
                            !popupRef.current?.contains(e.relatedTarget)
                        ) {
                            close();
                        }
                    }}
                >
                    {label}
                </StyledQuickFilterToggle>
            </BadgeWrapper>
            {isOpen && (
                <MultiSelectQuickFilterPopup
                    close={close}
                    maxHeight={maxHeight}
                    selectedValues={selectedValues}
                    onChangeValues={onChangeValues}
                    popupRef={popupRef}
                    triggerRef={triggerRef}
                    options={options}
                />
            )}
        </>
    );
}
